// src/components/reporting/AdHocAnalysis.js - Ad hoc conversation analysis tool with improved UX
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import { useReportPolling } from '../../hooks/useReportPolling';
import apiService from '../../services/apiService';
import '../../styles/globals.css';
import './Reporting.css';
import './AdHocAnalysis.css';

const AdHocAnalysis = ({ tenant, onBack, onViewReport }) => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const { loading, error, makeRequest } = useApiCall();

  // User selection
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Conversation selection
  const [conversations, setConversations] = useState([]);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [showConversationList, setShowConversationList] = useState(false);
  const [showQuestionCard, setShowQuestionCard] = useState(true);

  // Analysis prompt
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [promptError, setPromptError] = useState('');

  // Results
  const [reportId, setReportId] = useState(null);
  const [reportStatus, setReportStatus] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine conversation limit based on user role
  const isSuperAdmin = userProfile?.roles?.includes('SuperAdmin');
  const conversationLimit = isSuperAdmin ? 100 : 50;

  // Load users when component mounts
  useEffect(() => {
    if (tenant) {
      loadUsers();
    }
  }, [tenant]);

  const loadUsers = async () => {
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenant.id}/users?status=active`, user),
        'Loading users...'
      );

      if (data.success) {
        console.log('Users loaded:', data.data); // DEBUG
        const userList = data.data?.users || [];
        // Filter to only show users with at least 2 active conversations
        const filteredUsers = userList.filter(u => (u.conversationCount || 0) >= 2);
        setUsers(filteredUsers);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleUserSelect = async (userId) => {
    const selectedUserObj = users.find(u => u.id === parseInt(userId));
    
    setSelectedUser(selectedUserObj);
    setConversations([]);
    setSelectedConversationIds([]);
    setAnalysisPrompt('');
    setPromptError('');
    setReportData(null);
    setReportStatus(null);
    setShowConversationList(false);

    // Fetch user's conversations
    if (selectedUserObj) {
      await loadUserConversations(selectedUserObj.id);
    }
  };

  const loadUserConversations = async (userId) => {
    setLoadingConversations(true);
    try {
      const data = await apiService.get(
        `/api/v1/reports/useractivity/${userId}`,
        user
      );

      if (data.success && data.data?.conversations) {
        const convList = data.data.conversations;
        setConversations(convList);
        
        // Pre-select all conversations (up to limit)
        const idsToSelect = convList
          .slice(0, conversationLimit)
          .map(conv => conv.conversationId);
        setSelectedConversationIds(idsToSelect);
        
        // Auto-expand conversation list if conversations loaded
        if (convList.length > 0) {
          setShowConversationList(true);
        }
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleSelectAllConversations = () => {
    const allIds = conversations
      .slice(0, conversationLimit)
      .map(conv => conv.conversationId);
    setSelectedConversationIds(allIds);
  };

  const handleDeselectAllConversations = () => {
    setSelectedConversationIds([]);
  };

  const handleConversationToggle = (conversationId) => {
    setSelectedConversationIds(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId);
      } else {
        // Don't allow selecting more than the limit
        if (prev.length >= conversationLimit) {
          alert(`You can only select up to ${conversationLimit} conversations.`);
          return prev;
        }
        return [...prev, conversationId];
      }
    });
  };

  const handlePromptChange = (e) => {
    const value = e.target.value;
    setAnalysisPrompt(value);

    // Validate character count
    if (value.length > 5000) {
      setPromptError('Prompt must be 5000 characters or less');
    } else if (value.length < 10 && value.length > 0) {
      setPromptError('Prompt must be at least 10 characters');
    } else {
      setPromptError('');
    }
  };

  const handleSubmitAnalysis = async () => {
    // Validation
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }

    if (selectedConversationIds.length === 0) {
      alert('Please select at least one conversation');
      return;
    }

    if (analysisPrompt.length < 10) {
      alert('Analysis prompt must be at least 10 characters');
      return;
    }

    if (analysisPrompt.length > 5000) {
      alert('Analysis prompt must be 5000 characters or less');
      return;
    }

    setIsSubmitting(true);
    setReportData(null);
    setReportStatus('pending');

    // Hide conversation selector and question card when analysis starts
    setShowConversationList(false);
    setShowQuestionCard(false);

    try {
      // Determine analysis mode
      const analysisMode = selectedConversationIds.length === 1 ? 'individual' : 'group';

      // Create the report
      const createResponse = await apiService.post(
        '/api/v1/analytics/reports',
        {
          reportType: 'ad_hoc',
          title: `Ad Hoc Analysis - ${selectedUser.displayName || selectedUser.email}`,
          scope: {
            conversationIds: selectedConversationIds,
            analysisMode: analysisMode
          },
          prompt: analysisPrompt
        },
        user
      );

      if (createResponse.success && createResponse.data?.reportId) {
        // Setting reportId activates useReportPolling below (active depends
        // on isSubmitting && reportId; pollKey is reportId).
        setReportId(createResponse.data.reportId);
      } else {
        throw new Error(createResponse.message || 'Failed to create analysis report');
      }
    } catch (err) {
      console.error('Error submitting analysis:', err);
      setReportStatus('failed');
      setReportData({ error: err.message || 'Failed to submit analysis' });
      setIsSubmitting(false);
    }
  };

  // Status responses no longer carry the report body (Brief A) — the status
  // poll now only tells us pending/completed/failed/not_found. The 5-error
  // give-up threshold and the shorter 2-minute timeout that used to live
  // here are dropped in favor of the shared hook's totalTimeoutMs (4 min),
  // matching how the assessment and debrief flows already behave.
  const fetchAdHocStatus = useCallback(async (signal) => {
    try {
      const resp = await apiService.get(`/api/v1/analytics/reports/${reportId}`, user, { signal });
      const status = resp?.data?.status;
      if (status === 'completed') return { status: 'completed' };
      if (status === 'failed') return { status: 'failed' };
      if (status === 'not_found') return { status: 'failed', reason: 'not_found' };
      return { status: 'pending' };
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      // Stop immediately on rate limit rather than retrying on schedule,
      // matching the previous behavior — a deliberate exception to "return
      // null on transient errors."
      if (err?.isRateLimit) return { status: 'failed', reason: 'rate_limited' };
      console.error('[AdHocAnalysis] Status check failed:', err);
      return null;
    }
  }, [reportId, user]);

  const handleReportUpdate = useCallback((report) => {
    setReportStatus(report.status);

    if (report.status === 'completed') {
      setReportData(report);
      setIsSubmitting(false);
      // Navigate straight to ReportDetailPage for this report
      if (onViewReport) {
        onViewReport(reportId);
      }
    } else if (report.status === 'failed') {
      const message = report.reason === 'rate_limited'
        ? 'Rate limit exceeded while checking report status. The analysis may still be processing. Please wait a few minutes and check the Reporting Dashboard to see if your report has completed.'
        : report.reason === 'not_found'
        ? 'This report could not be found. It may have been removed.'
        : 'Analysis failed or could not be completed. Please try again or check the Reporting Dashboard.';
      setReportData({ error: { message } });
      setIsSubmitting(false);
    } else if (report.status === 'timed_out') {
      // Client gave up waiting; the server may still be finishing. Not a
      // failure — don't overwrite reportData with an error.
      setIsSubmitting(false);
    }
  }, [onViewReport, reportId]);

  useReportPolling({
    active: isSubmitting && !!reportId,
    pollKey: reportId,
    fetchStatus: fetchAdHocStatus,
    onUpdate: handleReportUpdate,
  });

  const handleReset = () => {
    setSelectedUser(null);
    setConversations([]);
    setSelectedConversationIds([]);
    setAnalysisPrompt('');
    setPromptError('');
    setReportData(null);
    setReportStatus(null);
    setReportId(null); // clearing pollKey deactivates useReportPolling
    setShowConversationList(false);
    setShowQuestionCard(true);
  };

  const isSubmitDisabled = 
    !selectedUser || 
    selectedConversationIds.length === 0 || 
    analysisPrompt.length < 10 || 
    analysisPrompt.length > 5000 ||
    isSubmitting;

  return (
    <div className="ad-hoc-analysis">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={onBack}
          className="btn btn-secondary btn-sm mb-4"
          disabled={isSubmitting}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Reports
        </button>

        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Ad Hoc Analysis</h2>
        <p className="text-gray-600">
          Ask questions about trends or patterns in user conversations
        </p>
        {tenant && (
          <div className="flex gap-2 mt-2" style={{ alignItems: 'center' }}>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-gray-600">{tenant.name}</span>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error mb-6">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="analysis-form">
        {/* Step 1: User Selection */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-800">
              Step 1: Select User
            </h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Choose a user to analyze</label>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="loading-dots mx-auto">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">Loading users...</p>
                </div>
              ) : (
                <select
                  className="form-control"
                  value={selectedUser?.id || ''}
                  onChange={(e) => handleUserSelect(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">-- Select a user --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.displayName || u.email} {u.displayName && `(${u.email})`}
                    </option>
                  ))}
                </select>
              )}
              
              {selectedUser && (
                <div className="selected-user-info">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Selected: {selectedUser.displayName || selectedUser.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Conversation Selection */}
        {selectedUser && (
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  Step 2: Select Conversations
                </h3>
                {conversations.length > 0 && (
                  <button
                    onClick={() => setShowConversationList(!showConversationList)}
                    className="btn btn-sm btn-secondary"
                    disabled={isSubmitting}
                  >
                    {showConversationList ? 'Hide' : 'Show'} List
                    <svg className={`w-4 h-4 ml-1 transition-transform ${showConversationList ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              {loadingConversations ? (
                <div className="text-center py-6">
                  <div className="loading-dots mx-auto">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                  <p className="text-gray-600 text-sm mt-2">Loading conversations...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-6">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-gray-600">This user has no conversations to analyze.</p>
                </div>
              ) : (
                <>
                  {/* Conversation selection summary */}
                  <div className="conversation-summary">
                    <div className="summary-stat">
                      <span className="stat-value">{selectedConversationIds.length}</span>
                      <span className="stat-label">of {conversations.length} selected</span>
                    </div>
                    {conversations.length > conversationLimit && (
                      <div className="summary-warning">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Max {conversationLimit} allowed
                      </div>
                    )}
                    <div className="summary-actions">
                      <button
                        onClick={handleSelectAllConversations}
                        className="btn btn-sm btn-secondary"
                        disabled={isSubmitting}
                      >
                        Select All
                      </button>
                      <button
                        onClick={handleDeselectAllConversations}
                        className="btn btn-sm btn-secondary"
                        disabled={isSubmitting}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Collapsible conversation list */}
                  {showConversationList && (
                    <div className="conversation-list-container">
                      <div className="conversation-list">
                        {conversations.map((conv, index) => {
                          const isSelected = selectedConversationIds.includes(conv.conversationId);
                          const isDisabled = index >= conversationLimit && !isSelected;
                          
                          return (
                            <label
                              key={conv.conversationId}
                              className={`conversation-line-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => !isDisabled && !isSubmitting && handleConversationToggle(conv.conversationId)}
                                disabled={isDisabled || isSubmitting}
                              />
                              <span className="conversation-line-title">
                                {conv.conversationTitle || 'Untitled Conversation'}
                              </span>
                              <span className="conversation-line-messages">
                                {conv.messageCount} msgs
                              </span>
                              <span className="conversation-line-date">
                                {new Date(conv.conversationCreatedAt).toLocaleDateString()}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Analysis Prompt */}
        {selectedUser && conversations.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-800">
                Step 3: Enter Analysis Question
              </h3>
              <button
                onClick={() => setShowQuestionCard(!showQuestionCard)}
                className="btn btn-sm btn-secondary"
              >
                {showQuestionCard ? 'Hide' : 'Show'} Question
                <svg className={`w-4 h-4 ml-1 transition-transform ${showQuestionCard ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {showQuestionCard && (
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">
                    What would you like to know about these conversations?
                  </label>
                  <textarea
                    className={`form-control analysis-textarea ${promptError ? 'border-red-500' : ''}`}
                    rows="10"
                    placeholder="Example: What are the most common challenges or pain points mentioned across these conversations?"
                    value={analysisPrompt}
                    onChange={handlePromptChange}
                    disabled={isSubmitting}
                  />
                  <div className="char-counter">
                    <span className={
                      analysisPrompt.length > 5000 ? 'text-red-600' :
                      analysisPrompt.length < 10 && analysisPrompt.length > 0 ? 'text-orange-600' :
                      'text-gray-500'
                    }>
                      {analysisPrompt.length} / 5,000 characters
                      {analysisPrompt.length < 10 && analysisPrompt.length > 0 && ' (minimum 10)'}
                    </span>
                    {promptError && (
                      <span className="text-red-600">
                        {promptError}
                      </span>
                    )}
                  </div>
                </div>

                {/* Submit button */}
                <div className="action-buttons">
                  <button
                    onClick={handleSubmitAnalysis}
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitDisabled}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-dots-inline">
                          <div className="loading-dot"></div>
                          <div className="loading-dot"></div>
                          <div className="loading-dot"></div>
                        </div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Run Analysis
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleReset}
                    className="btn btn-secondary"
                    disabled={isSubmitting}
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Display */}
        {reportStatus && (
          <div className="card results-card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-800">
                Analysis Results
              </h3>
            </div>
            <div className="card-body">
              {reportStatus === 'pending' || reportStatus === 'processing' ? (
                <div className="results-loading">
                  <div className="loading-dots mx-auto">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                  <p className="loading-title">
                    {reportStatus === 'pending' ? 'Preparing analysis...' : 'Analyzing conversations...'}
                  </p>
                  <p className="loading-subtitle">
                    This typically takes 15-30 seconds
                  </p>
                </div>
              ) : reportStatus === 'timed_out' ? (
                <div className="alert alert-warning">
                  <strong>Still Working</strong>
                  <p className="mt-2">
                    Analysis is taking longer than expected. Please check the Reporting Dashboard
                    in a moment to see if it completed.
                  </p>
                </div>
              ) : reportStatus === 'failed' ? (
                <div className="alert alert-error">
                  <strong>Analysis Failed</strong>
                  <p className="mt-2">
                    {reportData?.error?.message || reportData?.error || 'An error occurred during analysis'}
                  </p>
                  {reportData?.error?.details && (
                    <p className="text-sm mt-2 text-gray-600">
                      Details: {reportData.error.details}
                    </p>
                  )}
                </div>
              ) : reportStatus === 'completed' && reportData?.result ? (
                <div className="analysis-results">
                  {/* Executive Summary */}
                  {reportData.result.executiveSummary && (
                    <div className="result-section">
                      <h4 className="result-heading">
                        Executive Summary
                      </h4>
                      <div className="result-content summary-content">
                        {reportData.result.executiveSummary}
                      </div>
                    </div>
                  )}

                  {/* Key Findings */}
                  {reportData.result.keyFindings && (
                    <div className="result-section">
                      <h4 className="result-heading">
                        Key Findings
                      </h4>
                      <div className="result-content findings-content">
                        {reportData.result.keyFindings}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {reportData.result.recommendations && (
                    <div className="result-section">
                      <h4 className="result-heading">
                        Recommendations
                      </h4>
                      <div className="result-content recommendations-content">
                        {reportData.result.recommendations}
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  {reportData.metadata && (
                    <div className="result-metadata">
                      <div className="metadata-grid">
                        {reportData.metadata.conversationsAnalyzed && (
                          <div className="metadata-item">
                            <div className="metadata-label">Conversations Analyzed</div>
                            <div className="metadata-value">{reportData.metadata.conversationsAnalyzed}</div>
                          </div>
                        )}
                        {reportData.metadata.totalMessages && (
                          <div className="metadata-item">
                            <div className="metadata-label">Total Messages</div>
                            <div className="metadata-value">{reportData.metadata.totalMessages}</div>
                          </div>
                        )}
                        {reportData.metadata.estimatedCost && (
                          <div className="metadata-item">
                            <div className="metadata-label">Estimated Cost</div>
                            <div className="metadata-value">${reportData.metadata.estimatedCost.toFixed(4)}</div>
                          </div>
                        )}
                        {reportData.completedAt && (
                          <div className="metadata-item">
                            <div className="metadata-label">Completed At</div>
                            <div className="metadata-value">{new Date(reportData.completedAt).toLocaleString()}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AdHocAnalysis.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onViewReport: PropTypes.func.isRequired
};

export default AdHocAnalysis;